"use strict";
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function") __typeError("Object expected");
    var dispose, inner;
    if (async) dispose = value[__knownSymbol("asyncDispose")];
    if (dispose === void 0) {
      dispose = value[__knownSymbol("dispose")];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") __typeError("Object not disposable");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  };
  var fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e);
  var next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0]) return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError) throw error;
  };
  return next();
};
/**
 * @license
 * Copyright 2025 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { readKvStore } from "#src/kvstore/index.js";
import { pathIsDirectory } from "#src/kvstore/url.js";
import { ProgressSpan } from "#src/util/progress_listener.js";
export function simpleFilePresenceAutoDetectDirectorySpec(fileNames, match) {
  return {
    fileNames,
    match: async (options) => {
      const detectedFileNames = options.fileNames;
      for (const fileName of fileNames) {
        if (detectedFileNames.has(fileName)) {
          return [match];
        }
      }
      return [];
    }
  };
}
function composeMatchFunctions(specs) {
  return async (options) => {
    const matches = [];
    const results = await Promise.allSettled(
      specs.map((spec) => spec.match(options))
    );
    for (const result of results) {
      if (result.status !== "fulfilled") continue;
      matches.push(...result.value);
    }
    return matches;
  };
}
export function composeAutoDetectDirectorySpecs(specs) {
  const fileNames = /* @__PURE__ */ new Set();
  const subDirectories = /* @__PURE__ */ new Set();
  for (const spec of specs) {
    const { fileNames: curFileNames, subDirectories: curSubDirectories } = spec;
    if (curFileNames !== void 0) {
      for (const fileName of curFileNames) {
        fileNames.add(fileName);
      }
    }
    if (curSubDirectories !== void 0) {
      for (const subDirectory of curSubDirectories) {
        subDirectories.add(subDirectory);
      }
    }
  }
  return { fileNames, subDirectories, match: composeMatchFunctions(specs) };
}
export function composeAutoDetectFileSpecs(specs) {
  let prefixLength = 0;
  let suffixLength = 0;
  for (const spec of specs) {
    prefixLength = Math.max(prefixLength, spec.prefixLength);
    suffixLength = Math.max(suffixLength, spec.suffixLength);
  }
  return { prefixLength, suffixLength, match: composeMatchFunctions(specs) };
}
export class AutoDetectRegistry {
  directorySpecs = [];
  fileSpecs = [];
  _directorySpec;
  _fileSpec;
  registerDirectoryFormat(spec) {
    this.directorySpecs.push(spec);
    this._directorySpec = void 0;
  }
  registerFileFormat(spec) {
    this.fileSpecs.push(spec);
    this._fileSpec = void 0;
  }
  copyTo(registry) {
    registry.directorySpecs.push(...this.directorySpecs);
    registry.fileSpecs.push(...this.fileSpecs);
    registry._fileSpec = void 0;
    registry._directorySpec = void 0;
  }
  get directorySpec() {
    return this._directorySpec ?? (this._directorySpec = this.getDirectorySpec());
  }
  getDirectorySpec() {
    return composeAutoDetectDirectorySpecs(this.directorySpecs);
  }
  get fileSpec() {
    return this._fileSpec ?? (this._fileSpec = this.getFileSpec());
  }
  getFileSpec() {
    const { fileSpecs } = this;
    const specs = [...fileSpecs];
    return composeAutoDetectFileSpecs(specs);
  }
}
export async function autoDetectFormat(options) {
  var _stack = [];
  try {
    const kvStore = options.kvStoreContext.getKvStore(options.url);
    const { progressListener } = options;
    const _span = __using(_stack, progressListener && new ProgressSpan(progressListener, {
      message: `Auto-detecting data format at ${options.url}`
    }));
    if (!pathIsDirectory(kvStore.path) || kvStore.store.singleKey === true) {
      const statResponse = await kvStore.store.stat(kvStore.path, {
        signal: options.signal,
        progressListener: options.progressListener
      });
      if (statResponse !== void 0) {
        const autoDetectFile = options.autoDetectFile();
        const { totalSize } = statResponse;
        let prefix;
        let suffix;
        if (totalSize !== void 0 && autoDetectFile.suffixLength > 0) {
          if (totalSize <= autoDetectFile.prefixLength + autoDetectFile.suffixLength) {
            const readResponse = await readKvStore(kvStore.store, kvStore.path, {
              signal: options.signal,
              progressListener: options.progressListener,
              throwIfMissing: true
            });
            prefix = suffix = new Uint8Array(
              await readResponse.response.arrayBuffer()
            );
          } else {
            [prefix, suffix] = await Promise.all(
              [
                { offset: 0, length: autoDetectFile.prefixLength },
                {
                  offset: totalSize - autoDetectFile.suffixLength,
                  length: autoDetectFile.suffixLength
                }
              ].map(
                (byteRange) => readKvStore(kvStore.store, kvStore.path, {
                  signal: options.signal,
                  progressListener: options.progressListener,
                  throwIfMissing: true,
                  byteRange
                }).then((readResponse) => readResponse.response.arrayBuffer()).then((arrayBuffer) => new Uint8Array(arrayBuffer))
              )
            );
          }
        } else {
          prefix = new Uint8Array(
            await (await readKvStore(kvStore.store, kvStore.path, {
              signal: options.signal,
              progressListener: options.progressListener,
              throwIfMissing: true,
              byteRange: { offset: 0, length: autoDetectFile.prefixLength }
            })).response.arrayBuffer()
          );
        }
        return {
          matches: await autoDetectFile.match({
            url: options.url,
            prefix,
            suffix,
            totalSize,
            signal: options.signal
          }),
          url: options.url
        };
      }
      if (kvStore.store.singleKey === true) {
        return { matches: [], url: options.url };
      }
      kvStore.path += "/";
    }
    const autoDetectDirectory = options.autoDetectDirectory();
    const detectedFileNames = /* @__PURE__ */ new Set();
    await Promise.all(
      Array.from(autoDetectDirectory.fileNames ?? [], async (fileName) => {
        const response = await kvStore.store.stat(kvStore.path + fileName, {
          signal: options.signal,
          progressListener: options.progressListener
        });
        if (response !== void 0) {
          detectedFileNames.add(fileName);
        }
      })
    );
    const url = kvStore.store.getUrl(kvStore.path);
    const matches = await autoDetectDirectory.match({
      url,
      fileNames: detectedFileNames,
      subDirectories: /* @__PURE__ */ new Set(),
      signal: options.signal
    });
    return { matches, url };
  } catch (_) {
    var _error = _, _hasError = true;
  } finally {
    __callDispose(_stack, _error, _hasError);
  }
}
